import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Plus, 
  Receipt,
  Calendar,
  Trash2,
  DollarSign,
  ImageIcon,
  X,
  Filter,
  TrendingUp
} from 'lucide-react';
import { getExpenses, deleteExpense, updateExpenseReceipts, getExpensesByCategory } from '@/lib/api';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import ExpenseDialog from '@/components/dialogs/ExpenseDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const EXPENSE_CATEGORIES = [
  { value: 'all', label: 'All Categories' },
  { value: 'cleaning', label: 'Cleaning' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'supplies', label: 'Supplies' },
  { value: 'utilities', label: 'Utilities' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'taxes', label: 'Taxes' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'other', label: 'Other' },
];

const getCategoryColor = (category) => {
  const colors = {
    cleaning: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    maintenance: 'bg-amber-100 text-amber-700 border-amber-200',
    supplies: 'bg-blue-100 text-blue-700 border-blue-200',
    utilities: 'bg-purple-100 text-purple-700 border-purple-200',
    insurance: 'bg-pink-100 text-pink-700 border-pink-200',
    taxes: 'bg-red-100 text-red-700 border-red-200',
    marketing: 'bg-cyan-100 text-cyan-700 border-cyan-200',
    other: 'bg-slate-100 text-slate-700 border-slate-200',
  };
  return colors[category] || colors.other;
};

export const ExpensesView = ({ selectedProperty, properties, onRefresh }) => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [summary, setSummary] = useState({ by_category: {}, total: 0 });
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [newReceiptUrl, setNewReceiptUrl] = useState('');
  const [viewReceiptUrl, setViewReceiptUrl] = useState(null);

  const fetchExpenses = useCallback(async () => {
    try {
      setLoading(true);
      const category = categoryFilter === 'all' ? null : categoryFilter;
      const [expensesRes, summaryRes] = await Promise.all([
        getExpenses(selectedProperty?.id, category),
        getExpensesByCategory(selectedProperty?.id)
      ]);
      setExpenses(expensesRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching expenses:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedProperty, categoryFilter]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  const handleDelete = async () => {
    if (!expenseToDelete) return;
    
    try {
      await deleteExpense(expenseToDelete.id);
      toast.success('Expense deleted');
      fetchExpenses();
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete expense');
    } finally {
      setDeleteDialogOpen(false);
      setExpenseToDelete(null);
    }
  };

  const handleAddReceipt = async () => {
    if (!selectedExpense || !newReceiptUrl.trim()) return;
    
    try {
      const updatedReceipts = [...(selectedExpense.receipt_photos || []), newReceiptUrl.trim()];
      await updateExpenseReceipts(selectedExpense.id, updatedReceipts);
      toast.success('Receipt added');
      setNewReceiptUrl('');
      fetchExpenses();
      setSelectedExpense(prev => ({ ...prev, receipt_photos: updatedReceipts }));
    } catch (error) {
      toast.error('Failed to add receipt');
    }
  };

  const handleRemoveReceipt = async (receiptIndex) => {
    if (!selectedExpense) return;
    
    try {
      const updatedReceipts = (selectedExpense.receipt_photos || []).filter((_, i) => i !== receiptIndex);
      await updateExpenseReceipts(selectedExpense.id, updatedReceipts);
      toast.success('Receipt removed');
      fetchExpenses();
      setSelectedExpense(prev => ({ ...prev, receipt_photos: updatedReceipts }));
    } catch (error) {
      toast.error('Failed to remove receipt');
    }
  };

  const getPropertyName = (propertyId) => {
    const prop = properties.find(p => p.id === propertyId);
    return prop?.name || 'Unknown Property';
  };

  // Sort expenses by date (newest first)
  const sortedExpenses = [...expenses].sort((a, b) => 
    new Date(b.expense_date) - new Date(a.expense_date)
  );

  return (
    <div className="space-y-6" data-testid="expenses-view">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-manrope font-bold text-3xl text-slate-900 tracking-tight">
            Expenses & Receipts
          </h1>
          <p className="text-slate-500 mt-1">
            {selectedProperty ? selectedProperty.name : 'All Properties'} - Track spending and receipts
          </p>
        </div>
        <Button 
          onClick={() => setDialogOpen(true)}
          className="bg-slate-900 hover:bg-slate-800"
          data-testid="add-expense-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Expense
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-white border border-slate-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Expenses</p>
                <p className="text-2xl font-bold text-slate-900 font-manrope">
                  ${summary.total?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-slate-100">
                <DollarSign className="h-5 w-5 text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {Object.entries(summary.by_category || {}).slice(0, 3).map(([cat, amount]) => (
          <Card key={cat} className="bg-white border border-slate-100">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 capitalize">{cat}</p>
                  <p className="text-xl font-bold text-slate-900 font-manrope">
                    ${amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                <div className={`p-2 rounded-lg ${getCategoryColor(cat).split(' ')[0]}`}>
                  <TrendingUp className={`h-4 w-4 ${getCategoryColor(cat).split(' ')[1]}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filter */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-slate-500" />
          <span className="text-sm text-slate-600">Filter:</span>
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-48" data-testid="expense-filter-select">
            <SelectValue placeholder="Select category" />
          </SelectTrigger>
          <SelectContent>
            {EXPENSE_CATEGORIES.map((cat) => (
              <SelectItem key={cat.value} value={cat.value}>
                {cat.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Expenses List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner"></div>
        </div>
      ) : expenses.length === 0 ? (
        <Card className="bg-white border border-slate-100">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Receipt className="h-16 w-16 text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No expenses recorded</h3>
            <p className="text-slate-500 mb-4">Track your property expenses and keep receipts organized</p>
            <Button 
              onClick={() => setDialogOpen(true)}
              data-testid="add-first-expense-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Expense
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {sortedExpenses.map((expense) => (
            <Card 
              key={expense.id}
              className="bg-white border border-slate-100 card-hover"
              data-testid={`expense-card-${expense.id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium border ${getCategoryColor(expense.category)}`}>
                        {expense.category}
                      </span>
                      <span className="text-2xl font-bold text-slate-900 font-manrope">
                        ${expense.amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>

                    <h3 className="font-medium text-slate-800 mb-1">
                      {expense.description}
                    </h3>

                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {format(parseISO(expense.expense_date), 'MMM d, yyyy')}
                      </span>
                      {expense.vendor && (
                        <span>Vendor: {expense.vendor}</span>
                      )}
                      <span>{getPropertyName(expense.property_id)}</span>
                    </div>

                    {expense.notes && (
                      <p className="text-sm text-slate-600 bg-slate-50 p-2 rounded mt-3">
                        {expense.notes}
                      </p>
                    )}

                    {/* Receipt Thumbnails */}
                    {expense.receipt_photos && expense.receipt_photos.length > 0 && (
                      <div className="flex gap-2 mt-3 flex-wrap">
                        {expense.receipt_photos.slice(0, 4).map((photo, idx) => (
                          <img
                            key={`receipt-${photo.slice(-20)}-${idx}`}
                            src={photo}
                            alt={`Receipt ${idx + 1}`}
                            className="w-12 h-12 object-cover rounded cursor-pointer border border-slate-200 hover:border-slate-400 transition-colors"
                            onClick={() => setViewReceiptUrl(photo)}
                            onError={(e) => {
                              e.target.src = 'https://via.placeholder.com/48?text=!';
                            }}
                          />
                        ))}
                        {expense.receipt_photos.length > 4 && (
                          <div 
                            className="w-12 h-12 bg-slate-100 rounded flex items-center justify-center text-xs text-slate-600 cursor-pointer hover:bg-slate-200"
                            onClick={() => {
                              setSelectedExpense(expense);
                              setReceiptDialogOpen(true);
                            }}
                          >
                            +{expense.receipt_photos.length - 4}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedExpense(expense);
                        setReceiptDialogOpen(true);
                      }}
                      className="text-slate-600 hover:text-slate-900"
                      data-testid={`receipt-btn-${expense.id}`}
                    >
                      <Receipt className="h-4 w-4" />
                    </Button>

                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => {
                        setExpenseToDelete(expense);
                        setDeleteDialogOpen(true);
                      }}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-expense-${expense.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialogs */}
      <ExpenseDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          fetchExpenses();
          onRefresh();
        }}
        properties={properties}
        selectedProperty={selectedProperty}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Expense</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this expense of ${expenseToDelete?.amount?.toFixed(2)}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-expense-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-expense-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Receipt Management Dialog */}
      <Dialog open={receiptDialogOpen} onOpenChange={setReceiptDialogOpen}>
        <DialogContent className="sm:max-w-[500px]" data-testid="receipt-dialog">
          <DialogHeader>
            <DialogTitle className="font-manrope flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              Receipts - {selectedExpense?.description}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Add Receipt */}
            <div className="flex gap-2">
              <Input
                value={newReceiptUrl}
                onChange={(e) => setNewReceiptUrl(e.target.value)}
                placeholder="Paste receipt image URL..."
                className="flex-1"
                data-testid="add-receipt-input"
              />
              <Button 
                onClick={handleAddReceipt}
                disabled={!newReceiptUrl.trim()}
                data-testid="add-receipt-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
            </div>

            {/* Receipt Grid */}
            {selectedExpense?.receipt_photos && selectedExpense.receipt_photos.length > 0 ? (
              <div className="grid grid-cols-3 gap-3">
                {selectedExpense.receipt_photos.map((photo, idx) => (
                  <div key={`expense-receipt-${photo.slice(-20)}-${idx}`} className="relative group">
                    <img
                      src={photo}
                      alt={`Receipt ${idx + 1}`}
                      className="w-full h-24 object-cover rounded-lg border border-slate-200 cursor-pointer"
                      onClick={() => setViewReceiptUrl(photo)}
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/100?text=Invalid';
                      }}
                    />
                    <button
                      onClick={() => handleRemoveReceipt(idx)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`remove-receipt-${idx}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500 text-center py-8">
                No receipts uploaded. Add receipt photos for record keeping.
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Full Receipt View */}
      <Dialog open={!!viewReceiptUrl} onOpenChange={() => setViewReceiptUrl(null)}>
        <DialogContent className="sm:max-w-[800px] p-2">
          {viewReceiptUrl && (
            <img
              src={viewReceiptUrl}
              alt="Receipt"
              className="w-full h-auto rounded-lg"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExpensesView;
